import { Popover as PopoverPrimitive } from "@base-ui/react/popover"
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

const MAX_INTEGER = 2_147_483_647
export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const

export const formatPeriod = (year: number, month: number) =>
  `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}`

export const currentMonth = () => {
  const now = new Date()
  return formatPeriod(now.getFullYear(), now.getMonth() + 1)
}

export const parsePeriod = (value: string | undefined) => {
  const match = /^(\d{4,10})-(0[1-9]|1[0-2])$/.exec(value ?? "")
  if (!match) return null
  const year = Number(match[1])
  if (!Number.isInteger(year) || year < 1 || year > MAX_INTEGER) return null
  return {
    year,
    month: Number(match[2]),
    period: formatPeriod(year, Number(match[2])),
  }
}

export function MonthPicker({
  year,
  month,
  disabled,
  onSelect,
}: {
  year: number
  month: number
  disabled: boolean
  onSelect: (period: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [yearDraft, setYearDraft] = useState(String(year))
  useEffect(() => {
    if (open) setYearDraft(String(year))
  }, [open, year])
  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])
  const draftYear = Number(yearDraft)
  const validYear =
    yearDraft !== "" &&
    Number.isInteger(draftYear) &&
    draftYear >= 1 &&
    draftYear <= MAX_INTEGER
      ? draftYear
      : null
  const thisMonth = parsePeriod(currentMonth())!
  const choose = (nextYear: number, nextMonth: number) => {
    if (disabled) return
    onSelect(formatPeriod(nextYear, nextMonth))
    setOpen(false)
  }

  return (
    <PopoverPrimitive.Root
      open={open && !disabled}
      onOpenChange={(next) => setOpen(next && !disabled)}
    >
      <PopoverPrimitive.Trigger
        render={
          <Button
            type="button"
            variant="outline"
            className="w-full justify-between sm:w-64"
            disabled={disabled}
            aria-label={`Reporting month, ${MONTH_NAMES[month - 1]} ${year}`}
          />
        }
      >
        <span>
          {MONTH_NAMES[month - 1]} {year}
        </span>
        <CalendarIcon
          className="size-4 text-muted-foreground"
          aria-hidden="true"
        />
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Positioner sideOffset={6} className="z-50">
          <PopoverPrimitive.Popup className="w-[min(22rem,calc(100vw-2rem))] rounded-xl border bg-popover p-4 text-popover-foreground shadow-lg">
            <PopoverPrimitive.Title className="sr-only">
              Choose reporting month
            </PopoverPrimitive.Title>
            <div className="grid gap-4">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Previous year"
                  disabled={validYear === null || validYear === 1}
                  onClick={() => {
                    if (validYear !== null) setYearDraft(String(validYear - 1))
                  }}
                >
                  <ChevronLeft className="size-4" aria-hidden="true" />
                </Button>
                <Label htmlFor="activity-points-year" className="sr-only">
                  Year
                </Label>
                <Input
                  id="activity-points-year"
                  aria-label="Year"
                  type="number"
                  min={1}
                  max={MAX_INTEGER}
                  value={yearDraft}
                  onChange={(event) => setYearDraft(event.target.value)}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Next year"
                  disabled={validYear === null || validYear === MAX_INTEGER}
                  onClick={() => {
                    if (validYear !== null) setYearDraft(String(validYear + 1))
                  }}
                >
                  <ChevronRight className="size-4" aria-hidden="true" />
                </Button>
              </div>
              <div
                className="grid grid-cols-3 gap-2"
                role="group"
                aria-label={
                  validYear === null ? "Months" : `Months in ${validYear}`
                }
              >
                {MONTH_NAMES.map((name, index) => {
                  const value = index + 1
                  const selected = validYear === year && value === month
                  return (
                    <Button
                      key={name}
                      type="button"
                      variant={selected ? "default" : "ghost"}
                      aria-pressed={selected}
                      disabled={validYear === null}
                      onClick={() => {
                        if (validYear !== null) choose(validYear, value)
                      }}
                    >
                      {name.slice(0, 3)}
                    </Button>
                  )
                })}
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={year === thisMonth.year && month === thisMonth.month}
                onClick={() => choose(thisMonth.year, thisMonth.month)}
              >
                Current month
              </Button>
            </div>
          </PopoverPrimitive.Popup>
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
